/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import { useTable, usePagination, useSortBy, useRowSelect } from 'react-table';
import Popup from 'reactjs-popup';
import { connect } from 'react-redux';
import { CellTypes, ColumnHeaders } from '../../constants';

import Cell from './CellTypes/Cell';
import CheckBoxCell from './CellTypes/CheckBoxCell';
import UserCell from './CellTypes/UserCell';
import ActionCell from './CellTypes/ActionCell';
import DateCell from './CellTypes/DateCell';
import ListCell from './CellTypes/ListCell';
import styles from './UsersInfo.module.less';
import OpenSelect from '../../../../Components/OpenSelect/OpenSelect';
import HeadersBar from './HeadersBar';
import BulkActionsBar from './BulkActionBar';
import Checkbox from './Checkbox';
import RemoveMultipleUsersModal from '../ActionModals/RemoveMultipleUsersModal';
import { UsersSelectors } from '../../../../Redux/modules/users/selectors';
import { UsersActions } from '../../../../Redux/modules/users/actions';
import BulkAssignToTeamsModal from '../ActionModals/BulkAssignToTeamsModal';

const defaultColumn = {
  Cell,
  CheckBoxCell,
  ActionCell,
  UserCell,
  DateCell,
  ListCell,
};

function getCellType(cell, hover) {
  let type = '';

  switch (cell.column.Header) {
    case ColumnHeaders.ADMIN:
      type = CellTypes.CHECK_BOX_CELL;
      break;
    case ColumnHeaders.USERS:
      type = CellTypes.USER_CELL;
      break;
    case ColumnHeaders.TEAMS:
      type = CellTypes.LIST_CELL;
      break;
    case ColumnHeaders.SEARCH:
      type = CellTypes.CHECK_BOX_CELL;
      break;
    case ColumnHeaders.LAST_LOGIN:
      if (cell.row.id === hover.id) {
        type = CellTypes.ACTION_CELL;
        break;
      } else {
        type = CellTypes.DATE_CELL;
        break;
      }

    default:
      type = CellTypes.CELL;
  }
  return type;
}

function getRowStyle(header, cell, hover, row) {
  const style = [];
  if (header === ColumnHeaders.USERS) {
    style.push(styles.usersColumn);
  }
  if (header === ColumnHeaders.ADMIN) {
    style.push(styles.adminColumn);
  }
  if (header === ColumnHeaders.SEARCH) {
    style.push(styles.adminColumn);
  }
  if (header === ColumnHeaders.TEAMS) {
    style.push(styles.teamsColumn);
  }
  if (header === ColumnHeaders.LAST_LOGIN) {
    style.push(styles.cellCenterText);
  }
  if (hover.id === row.id) {
    style.push(styles.hoverRow);
  }
  if (cell.column.id === 'selection') {
    style.push(styles.selectionColumn);
    style.push(styles.selectionRow);
  }
  return style.join(' ');
}
const modalStyle = {
  width: '500px',
  boxShadow: 'rgba(101, 101, 101, 0.28) 0px 4px 12px 0px',
  borderRadius: '8px',
  border: 'none',
  padding: '40px 80px',
  backgroundColor: '#ffffff',
};

function UsersInfo({ columns, data, tableProps, fetchUsers, ...props }) {
  const allSelect = useRef(false);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    page,
    selectedFlatRows,
    toggleAllRowsSelected,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((clmns) => [
        // Let's make a column for selection
        {
          id: 'selection',
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: ({ getToggleAllPageRowsSelectedProps, getToggleAllRowsSelectedProps }) => (
            <div>
              <Checkbox
                {...getToggleAllPageRowsSelectedProps()}
                onChange={(e) => {
                  if (allSelect.current) {
                    getToggleAllRowsSelectedProps().onChange(e);
                  } else {
                    getToggleAllPageRowsSelectedProps().onChange(e);
                  }
                  if (getToggleAllRowsSelectedProps().checked) {
                    allSelect.current = false;
                  }
                }}
              />
            </div>
          ),
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div>
              <Checkbox {...row.getToggleRowSelectedProps()} />
            </div>
          ),
        },
        ...clmns,
      ]);
    },
  );

  const pageNumbers = () => {
    const TOTAL_SHOW = 5;
    const currentPage = pageIndex + 1;
    const middle = parseInt(TOTAL_SHOW / 2, 10);
    const startIndex =
      pageCount > TOTAL_SHOW
        ? Math.max(1, currentPage - middle) -
          (pageCount - currentPage < middle ? middle - (pageCount - currentPage) : 0)
        : 1;
    const endIndex = Math.min(pageCount, startIndex + TOTAL_SHOW);

    const numbers = [];
    for (let i = startIndex; i <= endIndex; i++) {
      numbers.push(i);
    }

    const startRange = startIndex - 1;

    return (
      <>
        {startRange >= middle - 1 && (
          <span
            tabIndex={0}
            role="button"
            onClick={() => gotoPage(startRange - 1)}
            className={startRange === currentPage ? styles.selectedPageNumber : styles.additionalPagesNumber}
            key={startRange}
          >
            ...
          </span>
        )}

        {numbers.map((pageNumber) => (
          <span
            tabIndex={0}
            role="button"
            onClick={() => gotoPage(pageNumber - 1)}
            className={pageNumber === currentPage ? styles.selectedPageNumber : styles.additionalPagesNumber}
            key={pageNumber}
          >
            {pageCount > TOTAL_SHOW &&
            pageNumber === endIndex &&
            (pageCount - middle > currentPage || pageNumber !== pageCount)
              ? '...'
              : pageNumber}
          </span>
        ))}
      </>
    );
  };

  function getButtonStyle(canPrevNext) {
    const buttonStyle = [styles.paginationButton];

    if (!canPrevNext) {
      buttonStyle.push(styles.disabledButton);
    }

    return buttonStyle.join(' ');
  }
  const options = [
    { label: '10 Per Page', value: 10 },
    { label: '50 Per Page', value: 50 },
    { label: '100 Per Page', value: 100 },
  ];
  const [isHover, setHover] = useState({ id: null });

  return (
    <div className={styles.tableContainer}>
      <table className={styles.usersTable} {...getTableProps()} id={`${props.type}-table`}>
        {selectedFlatRows.length > 0 ? (
          <BulkActionsBar
            headerGroups={headerGroups}
            selectedFlatRows={selectedFlatRows}
            pageSize={pageSize}
            dataLength={data.length}
            toggleAllRowsSelected={() => {
              toggleAllRowsSelected(true);
            }}
            setAllSelect={() => {
              allSelect.current = true;
            }}
          />
        ) : (
          <HeadersBar headerGroups={headerGroups} data={data} />
        )}

        {!props.isLoading && props.users.length > 0 ? (
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);

              return (
                <tr
                  {...row.getRowProps()}
                  id={`${props.type}-row-${i}`}
                  onMouseEnter={() => setHover({ id: row.id })}
                  onMouseLeave={() => setHover({ id: null })}
                >
                  {row.cells.map((cell) => {
                    return (
                      <td className={getRowStyle(cell.column.Header, cell, isHover, row)} {...cell.getCellProps()}>
                        {cell.render(getCellType(cell, isHover), {
                          tableProps,
                          isCurrentUserRow: props.currentLoggedEmail === cell?.row?.original?.email,
                        })}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        ) : null}
        <tfoot />
      </table>

      {props.isLoading && (
        <div style={{ fontSize: 20, lineHeight: 2, width: '100%', marginTop: 20 }}>
          <Skeleton count={10} />
        </div>
      )}

      {!props.isLoading && props.users.length === 0 && (
        <label className={styles.emptyTableText}>
          <label className={styles.emptyTableText}>{gettext('Oops… No users found, try another name or email')}</label>
        </label>
      )}
      {props.users.length > 0 && props.type === 'orgUsers' ? (
        <div className={styles.tableFooter}>
          <div className={styles.paginationContainer}>
            <button
              type="button"
              className={getButtonStyle(canPreviousPage)}
              onClick={previousPage}
              disabled={!canPreviousPage}
            >
              {'<< '}
              {gettext('Previous')}
            </button>
            <div className={styles.pageNumbersContainer}>{pageNumbers()}</div>
            <button type="button" className={getButtonStyle(canNextPage)} onClick={nextPage} disabled={!canNextPage}>
              {gettext('Next')}
              {' >>'}
            </button>
          </div>
          <div className={styles.paginationContainer}>
            <div style={{ width: 200 }}>
              <OpenSelect
                id="select-page-size"
                name="select-page-size"
                options={options}
                value={options.find((o) => o.value === pageSize)}
                setValue={(op) => setPageSize(op)}
              />
            </div>
          </div>
        </div>
      ) : null}
      <Popup
        onClose={() => props.clickedCloseModalButton()}
        contentStyle={modalStyle}
        modal
        open={props.removeMultipleUsersModalOpen}
        closeOnDocumentClick={false}
      >
        {() => <RemoveMultipleUsersModal selectedUsers={selectedFlatRows} />}
      </Popup>
      <Popup
        onClose={() => props.clickedCloseModalButton()}
        contentStyle={modalStyle}
        modal
        open={props.bulkAssignToTeamsModalOpen}
        closeOnDocumentClick={false}
      >
        {() => <BulkAssignToTeamsModal selectedUsers={selectedFlatRows} />}
      </Popup>
    </div>
  );
}
const mapStateToProps = (state) => {
  return {
    removeMultipleUsersModalOpen: UsersSelectors.removeMultipleUsersModalSelector(state),
    bulkAssignToTeamsModalOpen: UsersSelectors.bulkAssignToTeamsModalSelector(state),
  };
};

const mapDispatchToProps = {
  clickedCloseModalButton: UsersActions.clickedCloseModalButton,
};

UsersInfo.propTypes = {
  isLoading: PropTypes.bool,
  columns: PropTypes.array,
  data: PropTypes.array,
  tableProps: PropTypes.object,
  usersData: PropTypes.object,
  fetchUsers: PropTypes.func,

  setPage: PropTypes.func,
  nextPage: PropTypes.func,
  previousPage: PropTypes.func,
  currentPage: PropTypes.number,
  users: PropTypes.array,
  totalUsers: PropTypes.number,
  totalAdmins: PropTypes.number,
  totalInvitedUsers: PropTypes.number,
  type: PropTypes.string,
  isTrial: PropTypes.bool,

  currentLoggedEmail: PropTypes.string,
  getToggleAllRowsSelectedProps: PropTypes.func,
  getToggleAllPageRowsSelectedProps: PropTypes.func,
  toggleAllPageRowsSelected: PropTypes.func,
  row: PropTypes.object,
  removeMultipleUsersModalOpen: PropTypes.bool,
  bulkAssignToTeamsModalOpen: PropTypes.bool,
  clickedCloseModalButton: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersInfo);
